// react
import React, { useMemo } from "react";
import Highlighter from "react-highlight-words";

// UI
import { Row, Col, Table, Empty, message } from "antd";
import { HomePageTable } from "../../../Common/UIElements";
import CorporateContactUsersTopBar from "./CorporateContactUsersTopBar";
import LoadingComponent from "../../../Common/LoadingComponent";
import {
  ColoredTextTag,
  TEXT_TAG_COLORS,
} from "../../../Common/ColoredTextTag/ColoredTextTag";

// processing
import { tableStringSorter } from "../../../../utils/table";
import {
  useGetCustomer,
  useUpdateCustomer,
} from "../../../../hooks/Onboarding/onboarding.hooks";
import { useGetAllCorporateContacts } from "../../../../hooks/Onboarding/onboarding-corporate-contacts.hooks";
import {
  useActivateOnboardingUser,
  useInviteOnboardingUser,
} from "../../../../hooks/Onboarding/onboarding-users.hooks";

import useTableSearch from "../../../../hooks/table-search.hooks";
import {
  ONBOARDING_CORPORATE_CONTACT_STATUS,
  RECORD_OPERATIONS,
  ONBOARDING_CUSTOMER_STATUS,
} from "../../../../utils/onboarding-helper";
import { ButtonLink } from "../../../Common/Button";

// set table height according to window
let tableHeight = 500;
if (window.innerWidth > 700) {
  tableHeight = window.innerHeight - 375;
}

const textColor = (status) => {
  switch (status) {
    case ONBOARDING_CORPORATE_CONTACT_STATUS.ACTIVE:
      return <ColoredTextTag text={status} color={TEXT_TAG_COLORS.SUCCESS} />;
    case ONBOARDING_CORPORATE_CONTACT_STATUS.PENDING:
      return <ColoredTextTag text={status} color={TEXT_TAG_COLORS.WARN} />;
    default:
      return <ColoredTextTag text={status} color="black" />;
  }
};

const CorporateContactUsers = (props) => {
  const {
    addCorporateUser,
    customer,
    setCustomer,
    setOperation,
    setOperationData,
    openDrawer,
    drawerOpen,
  } = props;

  const {
    data: CorporateContactsData,
    isFetched: isFetchedCorporateContacts,
    refetch: refetchCorporateContacts,
  } = useGetAllCorporateContacts();
  const { data: apiCustomerData } = useGetCustomer(customer, {
    enabled: !!customer,
  });

  const { mutateAsync: updateCustomer } = useUpdateCustomer({
    onSuccess: () => {
      message.success("Corporate Customer Update in successfully");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });
  const { mutateAsync: activateCorporateContact } = useActivateOnboardingUser({
    onSuccess: () => {
      message.success("Activation request successfully sent");
      refetchCorporateContacts();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const { mutateAsync: inviteCorporateContact } = useInviteOnboardingUser({
    onSuccess: () => {
      message.success("Reactivation request successfully sent");
      refetchCorporateContacts();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const triggerDeactivateCorporateContact = (id) => {
    console.log("Deactivating ::", id);
  };

  const triggerActivateCorporateContact = async (id) => {
    apiCustomerData.status = ONBOARDING_CUSTOMER_STATUS.PENDING;
    const result = await updateCustomer(apiCustomerData);

    if (result.updatedCorporateContact._id) {
      activateCorporateContact({
        id,
        status: ONBOARDING_CUSTOMER_STATUS.PENDING,
      });
    }
  };

  const triggerResendInvitation = (id) => {
    inviteCorporateContact({ id });
  };

  // render component for hilightable search text
  const searchHilighter = (dataIndex) => ({
    render: (text) => {
      return searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={searchedWords}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      );
    },
  });

  // table columns
  const columns = [
    {
      title: "Corporate Contact First Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "250px",
      sorter: tableStringSorter("first_name"),
      ...searchHilighter("first_name"),
    },
    {
      title: "Corporate Contact Last Name",
      dataIndex: "last_name",
      key: "last_name",
      width: "250px",
      sorter: tableStringSorter("last_name"),
      ...searchHilighter("last_name"),
    },
    {
      title: "Corporate Contact Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
      width: "275px",
      sorter: tableStringSorter("phone_number"),
      ...searchHilighter("phone_number"),
    },
    {
      title: "Corporate Contact Email",
      dataIndex: "email",
      key: "email",
      width: "230px",
      sorter: tableStringSorter("email"),
      ...searchHilighter("email"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "100px",
      fixed: "right",
      sorter: tableStringSorter("status"),
      ...searchHilighter("status"),

      render: (idx, record) => textColor(record?.status),
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "_id",
      width: "200px",
      render: (_id, record) => {
        const { status } = record;
        return (
          <Row>
            <Col>
              {status === ONBOARDING_CORPORATE_CONTACT_STATUS.ACTIVE && (
                <ButtonLink
                  onClick={() => {
                    triggerDeactivateCorporateContact(_id);
                  }}
                  disabled
                >
                  <span style={{ textDecoration: "underline" }}>
                    Deactivate
                  </span>
                </ButtonLink>
              )}
              {(status === ONBOARDING_CORPORATE_CONTACT_STATUS.IN_DRAFT ||
                (status !== ONBOARDING_CORPORATE_CONTACT_STATUS.PENDING &&
                  status !== ONBOARDING_CORPORATE_CONTACT_STATUS.ACTIVE)) && (
                <ButtonLink
                  onClick={() => {
                    triggerActivateCorporateContact(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>Activate</span>
                </ButtonLink>
              )}
              {status === ONBOARDING_CORPORATE_CONTACT_STATUS.PENDING && (
                <ButtonLink
                  onClick={() => {
                    triggerResendInvitation(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>
                    Resend Invitation
                  </span>
                </ButtonLink>
              )}
              {(status === ONBOARDING_CORPORATE_CONTACT_STATUS.IN_DRAFT ||
                status !== ONBOARDING_CORPORATE_CONTACT_STATUS.PENDING) && (
                <ButtonLink
                  onClick={() => {
                    setOperation(RECORD_OPERATIONS.EDIT);
                    setOperationData(record);
                    openDrawer();
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>Edit</span>
                </ButtonLink>
              )}
            </Col>
          </Row>
        );
      },
      fixed: "right",
    },
  ];

  // columns that could be searched
  const searchableColumns = columns.map((col) => col.dataIndex);

  // custom hook for external multi column table data searching
  const {
    filteredTableData,
    searchedColumns,
    searchedWords,
    updateSearchMeta,
    searchMeta,
  } = useTableSearch({
    searchableColumns,
    allData: CorporateContactsData ?? [],
  });

  const corporateUsersData = useMemo(() => {
    let CorporateContacts;
    if (Object.keys(searchMeta).length > 0 && filteredTableData) {
      CorporateContacts = [...filteredTableData];
    } else {
      CorporateContacts = CorporateContactsData;
    }
    return CorporateContacts;
  }, [CorporateContactsData, searchMeta, filteredTableData]);

  const isCorporateContactActivated =
    corporateUsersData?.filter(
      (user) => user.status === ONBOARDING_CORPORATE_CONTACT_STATUS.ACTIVE,
    ).length > 0;

  return (
    <>
      <CorporateContactUsersTopBar
        customer={customer}
        setCustomer={setCustomer}
        updateSearchMeta={updateSearchMeta}
        isFetchedCorporateContacts={isFetchedCorporateContacts}
        addCorporateUser={addCorporateUser}
        disableAddCorporateUser={isCorporateContactActivated}
      />
      {!Array.isArray(corporateUsersData) || corporateUsersData.length === 0 ? (
        <Empty description="No Corporate Contact Found" />
      ) : (
        <div>
          <HomePageTable>
            <Table
              className="onboarding-corporate-contacts-table"
              columns={columns}
              dataSource={corporateUsersData}
              rowKey="_id"
              scroll={{ x: 1100, y: tableHeight }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
              locale={{
                emptyText: isFetchedCorporateContacts ? (
                  <div className="spinner-table">
                    <LoadingComponent />
                  </div>
                ) : null,
              }}
            />
          </HomePageTable>
        </div>
      )}
    </>
  );
};

export default CorporateContactUsers;
